/**
 * B-555 / B-556 — kiedy otwarta aplikacja wymienia się na najnowszą wersję? (WSPÓLNY RDZEŃ)
 *
 * Port mechanizmu z aplikacji kierowcy school-transport (Driver 1.49.0, `lib/appUpdate.ts`),
 * użyty w obu aplikacjach ZUR OPS:
 *   • aplikacja instruktora (PWA)  — Instruktor 1.261.0, `pwa/src/lib/aktualizacja.ts`,
 *   • panel administratora         — Admin 1.602.0, `frontend/src/shared/lib/aktualizacja.ts`.
 * LUSTRO: oba pliki są identyczne co do bajtu (pilnuje tego `scripts/__tests__/aktualizacja-panel.test.mjs`).
 * Różni się tylko lista bezpiecznych ekranów — ta żyje obok, w `aktualizacjaEkrany.ts` każdej aplikacji.
 *
 * ── Po co ────────────────────────────────────────────────────────────────────
 * Karta (albo zainstalowana aplikacja), która została otwarta przed wdrożeniem, biegnie na STARYM
 * kodzie, dopóki ktoś jej nie odświeży. Aplikacja pyta więc własny serwer, jaka wersja stoi za hostem
 * (`/version.json`), a ta funkcja rozstrzyga, co z odpowiedzią zrobić.
 *
 * Czysta na celowo — bez Reacta, DOM, zegara i storage — żeby reguły szły pod zwykłym `node`, a
 * komponent był cienki. To, czy ekran jest „zajęty”, sprawdza komponent; CO z tym zrobić, rozstrzyga
 * się tutaj.
 *
 * Trzy wyniki:
 *   • 'none'   — aplikacja jest tym, co stoi na serwerze (albo serwer nie dał nic wiarygodnego).
 *   • 'banner' — jest inna wersja; pokaż nieblokujący przycisk „Aktualizuj”.
 *   • 'reload' — wymień stronę teraz, bez pytania. WYŁĄCZNIE w bezpiecznym momencie:
 *                przeładowanie wyrzuca to, co jest na ekranie i w pamięci.
 */

/** Powrót z tła trwający co najmniej tyle, zanim w ogóle rozważymy przeładowanie. Krótkie
 *  przełączenia (rozmowa, rzut oka na inną kartę) nie mogą mrugać ekranem; telefon, który leżał w
 *  kieszeni, albo karta, do której wracasz po przerwie, jest właśnie tym momentem. */
export const RESUME_MIN_HIDDEN_MS = 30_000;

interface ParsedVersion { nums: number[]; pre: string }

/** `1.261.0`, `v1.261.0`, `1.261.0-beta.1` — wszystko inne nie jest wersją. */
function parseVersion(raw: string): ParsedVersion | null {
  const m = /^v?(\d+(?:\.\d+)*)(?:[-+]([0-9A-Za-z.+-]+))?$/.exec(raw.trim());
  if (!m) return null;
  return { nums: m[1].split('.').map(Number), pre: m[2] ?? '' };
}

/**
 * -1 gdy `a` starsza od `b`, 1 gdy nowsza, 0 gdy ta sama. Części liczbowe porównywane jako liczby
 * (1.9.0 < 1.10.0), brakująca część to 0 (1.261 == 1.261.0). Wersja wstępna jest przed wydaniem.
 * Napisy, które nie są wersjami, porównują się jako tekst — równe tylko gdy identyczne.
 */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa || !pb) {
    const x = a.trim();
    const y = b.trim();
    return x === y ? 0 : x < y ? -1 : 1;
  }
  const len = Math.max(pa.nums.length, pb.nums.length);
  for (let i = 0; i < len; i++) {
    const x = pa.nums[i] ?? 0;
    const y = pb.nums[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  if (pa.pre === pb.pre) return 0;
  if (pa.pre === '') return 1;
  if (pb.pre === '') return -1;
  return pa.pre < pb.pre ? -1 : 1;
}

/**
 * Wersja z JSON-a `/version.json` albo null, gdy odpowiedzi nie można użyć. Portal przechwytujący
 * albo błąd proxy potrafi odpowiedzieć 200 czymś zupełnie innym; uznanie tego za „nową wersję”
 * wywołałoby baner (albo przeładowanie) z niczego — liczy się tylko poprawna wersja.
 */
export function readServerVersion(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const v = (body as { version?: unknown }).version;
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return parseVersion(trimmed) ? trimmed : null;
}

export type UpdateDecision = 'none' | 'banner' | 'reload';

export interface UpdateInput {
  /** Co odpowiedziało `/version.json` (już przez `readServerVersion`); null przy błędzie. */
  serverVersion: string | null;
  /** Wersja, którą działa ta strona (`APP_VERSION`). */
  clientVersion: string;
  /**
   * Jak długo karta była ukryta tuż przed tym sprawdzeniem, w ms — ustawione WYŁĄCZNIE, gdy
   * sprawdzenie jest powrotem na pierwszy plan. null dla reszty: pierwsze sprawdzenie po świeżym
   * załadowaniu (ta strona jest już najnowszą rzeczą, jaką serwer wysłał, więc nigdy się jej nie
   * przeładowuje), `focus`, `online`, zegar. Tylko prawdziwy powrót z tła to moment, w którym
   * przeładowanie jest oczekiwane.
   */
  hiddenForMs: number | null;
  /** Czy bieżący ekran jest na liście bezpiecznych (decyduje o tym `aktualizacjaEkrany.ts` aplikacji). */
  safeScreen: boolean;
  /** Coś jeszcze czeka na wysłanie albo właśnie się wysyła (kolejka offline, szkice, zapis w toku).
   *  Przeładowanie w połowie wysłałoby część z tego drugi raz albo urwało zapis. */
  hasUnsyncedWork: boolean;
  /** Otwarte okno albo arkusz, wpisany tekst w polu — wszystko, co przeładowanie by skasowało. */
  uiBusy: boolean;
  online: boolean;
  /** Wersja serwera, dla której przeładowanie było już próbowane w tej karcie (sessionStorage), albo
   *  null. Straż przed pętlą: jeśli tamto przeładowanie nie doprowadziło klienta do wersji serwera
   *  (stara kopia nadal skądś serwowana), kolejne zapętliłoby się w nieskończoność. */
  reloadAttemptedFor: string | null;
}

export function decideUpdate(input: UpdateInput): UpdateDecision {
  const { serverVersion, clientVersion } = input;
  if (!serverVersion) return 'none';
  /* KAŻDA różnica się liczy, nie tylko „nowsza”: wycofanie złego wydania musi dotrzeć także do
     tych, którzy to złe wydanie zdążyli pobrać. */
  if (compareVersions(serverVersion, clientVersion) === 0) return 'none';

  const justResumed = input.hiddenForMs !== null && input.hiddenForMs >= RESUME_MIN_HIDDEN_MS;
  const safeMoment =
    justResumed &&
    input.online &&
    input.safeScreen &&
    !input.hasUnsyncedWork &&
    !input.uiBusy &&
    input.reloadAttemptedFor !== serverVersion;

  return safeMoment ? 'reload' : 'banner';
}

/* ── Co jest „zajęte” na ekranie ─────────────────────────────────────────── */

/** Typy pól, w których wpisany tekst jest czymś, co użytkownik by stracił. */
export const TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  '', 'text', 'search', 'email', 'tel', 'url', 'number', 'password', 'date', 'time', 'datetime-local',
]);

/** Najmniejszy kawałek DOM, jakiego potrzebuje `czyUiZajete` — pozwala przetestować funkcję na atrapie. */
export interface FieldLike {
  tagName: string;
  type?: string;
  value?: string;
  isContentEditable?: boolean;
}
export interface DocLike {
  querySelector(selector: string): unknown;
  querySelectorAll(selector: string): ArrayLike<FieldLike>;
  activeElement: FieldLike | null;
}

/** Selektory okien i arkuszy. ZUR rysuje arkusze jako `fixed inset-0`, a nie zawsze jako `aria-modal`,
 *  więc same role nie wystarczą — brakujący arkusz to fałszywe „nic otwartego”, a ono kosztuje
 *  więcej niż niepotrzebny baner. */
export const OVERLAY_SELECTOR =
  '[aria-modal="true"], [role="dialog"], [role="alertdialog"], .fixed.inset-0';

/**
 * Cokolwiek na ekranie, co przeładowanie by skasowało, a czego żadna kolejka nie trzyma:
 *   • otwarte okno albo arkusz (wybór, kończenie, potwierdzenie, mapa, podpis…),
 *   • pole, w które się pisze — albo takie, które nadal ma tekst, choć karta poszła w tło
 *     i straciło fokus (szkic wiadomości, wyszukiwarka).
 * Fałszywy alarm jest tani (pokazuje się baner), a fałszywy brak alarmu zjada pół napisanej wiadomości.
 */
export function czyUiZajete(doc: DocLike): boolean {
  if (doc.querySelector(OVERLAY_SELECTOR)) return true;

  const active = doc.activeElement;
  if (active) {
    if (active.isContentEditable) return true;
    const tag = active.tagName.toUpperCase();
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (tag === 'INPUT' && TEXT_INPUT_TYPES.has((active.type ?? '').toLowerCase())) return true;
  }

  const fields = doc.querySelectorAll('textarea, input');
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (f.tagName.toUpperCase() === 'INPUT' && !TEXT_INPUT_TYPES.has((f.type ?? '').toLowerCase())) continue;
    if ((f.value ?? '').trim() !== '') return true;
  }
  return false;
}
